/**
 * Sound and voice.
 * ---------------------------------------------------------------------------
 * Every sound is synthesised with the Web Audio API rather than loaded from a
 * file. That is a deliberate trade: zero network bytes, zero decode latency,
 * nothing to cache, and no licensing questions on a children's product. The
 * cost is that the palette is simple — which for keystroke feedback is exactly
 * what you want anyway.
 *
 * Voice coaching uses the browser's built-in speech synthesis, so it also ships
 * no audio assets and works offline.
 *
 * Autoplay policy: the AudioContext is created lazily on the first real user
 * gesture and resumed if the browser suspended it.
 */

type SoundName =
  | 'key'
  | 'space'
  | 'error'
  | 'correct'
  | 'star'
  | 'levelUp'
  | 'coin'
  | 'badge'
  | 'complete'
  | 'chest';

interface Tone {
  freq: number;
  /** Seconds. */
  duration: number;
  type: OscillatorType;
  /** 0–1, before the master volume is applied. */
  gain: number;
  /** Seconds to wait before playing — used to build little arpeggios. */
  delay?: number;
  /** Slide to this frequency over the note's duration. */
  slideTo?: number;
}

/**
 * Note recipes.
 *
 * On amplitude: these were originally authored around 0.05–0.10, which after
 * the 0.5 master gain landed near 0.03 — roughly 30 dB below a normal interface
 * sound and inaudible on laptop speakers. They are now in the 0.18–0.40 band,
 * which lands near 0.09–0.20 after the master. The relative balance is the
 * point: a keystroke fires hundreds of times a minute so it stays the quietest
 * thing here, and the reward sounds are roughly twice its level.
 *
 * Nothing sums above 1.0: notes within a recipe are sequenced with delays, and
 * the two that briefly overlap peak around 0.7.
 */
const RECIPES: Record<SoundName, Tone[]> = {
  key: [{ freq: 620, duration: 0.03, type: 'square', gain: 0.2 }],
  space: [{ freq: 320, duration: 0.038, type: 'square', gain: 0.22 }],
  error: [
    { freq: 190, duration: 0.09, type: 'sawtooth', gain: 0.3 },
    { freq: 140, duration: 0.1, type: 'sawtooth', gain: 0.24, delay: 0.05 },
  ],
  correct: [{ freq: 880, duration: 0.07, type: 'sine', gain: 0.28 }],
  star: [
    { freq: 880, duration: 0.09, type: 'sine', gain: 0.34 },
    { freq: 1175, duration: 0.11, type: 'sine', gain: 0.34, delay: 0.08 },
  ],
  coin: [
    { freq: 1319, duration: 0.06, type: 'square', gain: 0.26 },
    { freq: 1760, duration: 0.09, type: 'square', gain: 0.22, delay: 0.05 },
  ],
  badge: [
    { freq: 659, duration: 0.1, type: 'triangle', gain: 0.34 },
    { freq: 880, duration: 0.1, type: 'triangle', gain: 0.34, delay: 0.09 },
    { freq: 1319, duration: 0.22, type: 'triangle', gain: 0.34, delay: 0.18 },
  ],
  levelUp: [
    { freq: 523, duration: 0.11, type: 'triangle', gain: 0.36 },
    { freq: 659, duration: 0.11, type: 'triangle', gain: 0.36, delay: 0.1 },
    { freq: 784, duration: 0.11, type: 'triangle', gain: 0.36, delay: 0.2 },
    { freq: 1047, duration: 0.32, type: 'triangle', gain: 0.38, delay: 0.3 },
  ],
  complete: [
    { freq: 784, duration: 0.12, type: 'sine', gain: 0.36 },
    { freq: 988, duration: 0.12, type: 'sine', gain: 0.36, delay: 0.1 },
    { freq: 1319, duration: 0.3, type: 'sine', gain: 0.38, delay: 0.2 },
  ],
  chest: [
    { freq: 392, duration: 0.14, type: 'triangle', gain: 0.34, slideTo: 784 },
    { freq: 1047, duration: 0.26, type: 'sine', gain: 0.34, delay: 0.14 },
  ],
};

class KidsAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private enabled = true;
  /** Guards against queueing a pile of sounds behind a single pending resume. */
  private resuming = false;
  private volume = 0.5;
  private voiceEnabled = false;

  configure(opts: { sound?: boolean; volume?: number; voice?: boolean }) {
    if (opts.sound !== undefined) this.enabled = opts.sound;
    if (opts.voice !== undefined) this.voiceEnabled = opts.voice;
    if (opts.volume !== undefined) {
      this.volume = Math.max(0, Math.min(1, opts.volume));
      if (this.master && this.ctx) {
        this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.01);
      }
    }
    if (!this.enabled) this.stopSpeech();
  }

  /**
   * Create/resume the AudioContext from a real user gesture.
   *
   * Chrome grants user activation on keydown, so typing alone is enough there.
   * Safari is stricter and wants a pointer event, so the lesson also calls this
   * on the first tap of the exercise. Safe to call as often as you like.
   */
  unlock() {
    this.ensureContext();
  }

  /**
   * Turn sound on/off and immediately confirm it audibly.
   *
   * The confirmation matters more than it looks: the setting used to be a
   * silent boolean, so a user who pressed the speaker icon heard nothing and
   * reasonably concluded the audio was broken. Playing a blip on the same
   * gesture both proves it works and unlocks the AudioContext at the ideal
   * moment. `enabled` is set directly rather than waiting for React state to
   * round-trip, because the blip has to happen inside this gesture.
   */
  setEnabled(on: boolean) {
    this.enabled = on;
    if (on) this.play('correct');
    else this.stopSpeech();
  }

  /** Must be called from a user gesture the first time, per autoplay policy. */
  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.volume;
        this.master.connect(this.ctx.destination);
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  play(name: SoundName) {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.master) return;

    if (ctx.state === 'running') {
      this.schedule(name, ctx);
      return;
    }

    // A suspended context has a frozen `currentTime` of 0, so notes scheduled
    // against it all land at the same instant and fire together the moment it
    // resumes — a sequence collapses into one splat. Wait for the resume, then
    // schedule against a clock that is actually moving.
    //
    // Only one sound is ever queued this way. If a child types six characters
    // before the context wakes, queuing six clicks would just move the pile-up
    // rather than fix it, so the rest are dropped: a keystroke tick is
    // disposable, and by the next keystroke the context is running.
    if (this.resuming) return;
    this.resuming = true;
    void ctx
      .resume()
      .then(() => {
        this.resuming = false;
        if (this.enabled) this.schedule(name, ctx);
      })
      .catch(() => {
        this.resuming = false;
      });
  }

  private schedule(name: SoundName, ctx: AudioContext) {
    if (!this.master) return;

    for (const tone of RECIPES[name]) {
      const start = ctx.currentTime + (tone.delay ?? 0);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = tone.type;
      osc.frequency.setValueAtTime(tone.freq, start);
      if (tone.slideTo)
        osc.frequency.exponentialRampToValueAtTime(tone.slideTo, start + tone.duration);

      // A hard start or stop on a gain node produces an audible click, so both
      // ends are ramped. 4 ms attack is below the threshold of feeling slow.
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(tone.gain, start + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration);

      osc.connect(gain).connect(this.master);
      osc.start(start);
      osc.stop(start + tone.duration + 0.02);
    }
  }

  /** Speak a coaching line. No-op when voice is off or unsupported. */
  speak(text: string, opts: { interrupt?: boolean } = {}) {
    if (!this.voiceEnabled || typeof window === 'undefined') return;
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (opts.interrupt) synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    // Slightly slow and slightly high — clearer for young listeners.
    utterance.rate = 0.92;
    utterance.pitch = 1.15;
    utterance.volume = this.volume;
    utterance.lang = 'en-US';
    synth.speak(utterance);
  }

  stopSpeech() {
    if (typeof window === 'undefined') return;
    window.speechSynthesis?.cancel();
  }

  /** Called when the player unmounts so nothing keeps talking. */
  dispose() {
    this.stopSpeech();
  }
}

/** One instance for the whole module — audio contexts are a limited resource. */
export const kidsAudio = new KidsAudio();

/** Is spoken coaching available in this browser at all? */
export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}
